import {
    AssignmentGenerationModel,
    CalculationExecutionModel,
    CalculationFindingPageModel,
    CalculationPreviewModel,
    CalculationPreviewPageModel,
    CalculationPreviewPageRequest,
    CalculationRunOptionsModel,
    CalculationRunSummaryModel,
    CreateCalculationProcedureModel,
    CreateProcedureAssignmentModel,
    ExactSelectorResolutionModel,
    ProcedureAssignmentListItemModel,
    ProcedureDraftWriteModel,
    ProcedureInputSelectorWriteModel,
    ProcedureModel,
    ProcedureRuleWriteModel,
    ProcedureSourceModuleSelectorWriteModel,
    ProcedureTargetSelectorWriteModel,
    ProcedureVersionModel,
    ProcedureVersionSummaryModel,
    RenameCalculationProcedureModel,
    ResolveExactSelectorsModel,
    ScopedCalculationRequest,
    UpgradeProcedureAssignmentModel,
} from "../api/APIClient";
import { calculationFindingsClient, calculationProceduresClient, calculationsClient, procedureAssignmentsClient } from "../api/clients";
import { CalculationScope, toScopeModels } from "../calculations/scopes";
import { ProcedureDraft } from "./vocabulary";

/**
 * Calculation procedures, their assignments, and running the engine.
 *
 * The shape of the thing being edited:
 *
 *   CalculationProcedure          a stable family, e.g. "Term mark from division marks"
 *     -> CalculationProcedureVersion   Draft or Published; immutable once published
 *          -> target selector + method + missing-mark policy
 *          -> CalculationProcedureRule[]  selector-based input definitions
 *
 * An assignment binds one published version to one qualification module. Activating or regenerating
 * it expands the rules into concrete `Calculation` rows - the actual target/component/weight edges
 * the engine walks. Those rows are generated, never hand-authored, which is why nothing here writes
 * one.
 */

export type {
    ProcedureModel,
    ProcedureVersionModel,
    ProcedureVersionSummaryModel,
    ProcedureAssignmentListItemModel,
    AssignmentGenerationModel,
    ExactSelectorResolutionModel,
    CalculationRunSummaryModel,
    CalculationPreviewPageModel,
    CalculationFindingPageModel,
};

// ------------------------------------------------------------------ procedures

export const listProcedures = (includeArchived = false): Promise<ProcedureModel[]> =>
    calculationProceduresClient.list(includeArchived);

export const createProcedure = (name: string) => calculationProceduresClient.create(new CreateCalculationProcedureModel({ name }));

export const renameProcedure = (procedureID: string, name: string): Promise<ProcedureModel> =>
    calculationProceduresClient.rename(procedureID, new RenameCalculationProcedureModel({ name }));

export const archiveProcedure = (procedureID: string): Promise<ProcedureModel> => calculationProceduresClient.archive(procedureID);

export const listVersions = (procedureID: string): Promise<ProcedureVersionSummaryModel[]> =>
    calculationProceduresClient.listVersions(procedureID);

export const getVersion = (versionID: string): Promise<ProcedureVersionModel> => calculationProceduresClient.getVersion(versionID);

/** Converts the editor's flat draft into the nested write model the API expects. */
function toWriteModel(draft: ProcedureDraft): ProcedureDraftWriteModel {
    return new ProcedureDraftWriteModel({
        targetMarkTypeID: draft.targetMarkTypeID,
        calculationMethod: draft.calculationMethod,
        missingMarkPolicy: draft.missingMarkPolicy,
        gradeScaleID: draft.gradeScaleID,
        targetSelector: new ProcedureTargetSelectorWriteModel({
            kind: draft.targetSelectorKind,
            path: draft.targetPath,
        }),
        rules: draft.rules.map(
            (rule) =>
                new ProcedureRuleWriteModel({
                    id: rule.id,
                    componentMarkTypeID: rule.componentMarkTypeID,
                    weight: rule.weight,
                    sourceModuleSelector: new ProcedureSourceModuleSelectorWriteModel({
                        kind: rule.sourceModuleKind,
                        moduleID: rule.sourceModuleID,
                    }),
                    inputSelector: new ProcedureInputSelectorWriteModel({
                        anchor: rule.inputAnchor,
                        kind: rule.inputKind,
                        groupPath: rule.groupPath,
                        assessmentTypeID: rule.assessmentTypeID,
                        assessmentGroupTypeID: rule.assessmentGroupTypeID,
                        concreteEntityType: rule.concreteEntityType,
                        concreteEntityID: rule.concreteEntityID,
                    }),
                })
        ),
    });
}

export const saveDraft = (procedureID: string, draft: ProcedureDraft): Promise<ProcedureVersionModel> =>
    calculationProceduresClient.saveDraft(procedureID, toWriteModel(draft));

/** Returns the draft with `findings` populated. Validation never mutates anything. */
export const validateDraft = (procedureID: string): Promise<ProcedureVersionModel> =>
    calculationProceduresClient.validateDraft(procedureID);

/** Publishing freezes the version. It needs its own permission, above authoring one. */
export const publishDraft = (procedureID: string): Promise<ProcedureVersionModel> => calculationProceduresClient.publish(procedureID);

export const discardDraft = (procedureID: string): Promise<boolean> => calculationProceduresClient.discardDraft(procedureID);

/** Start a new draft from a published version - the only way to change a published procedure. */
export const copyVersionToDraft = (procedureID: string, versionID: string): Promise<ProcedureVersionModel> =>
    calculationProceduresClient.copyToDraft(procedureID, versionID);

/**
 * Dry-runs the version's selectors against one module and reports what they would actually match.
 * The fastest way to find out that a rule matches nothing because a group type is missing.
 */
export const resolveExactSelectors = (versionID: string, qualificationModuleID: string): Promise<ExactSelectorResolutionModel> =>
    calculationProceduresClient.resolveExactSelectors(versionID, new ResolveExactSelectorsModel({ qualificationModuleID }));

// ----------------------------------------------------------------- assignments

export const listAssignments = (filter: { qualificationModuleID?: string; procedureID?: string } = {}): Promise<
    ProcedureAssignmentListItemModel[]
> => procedureAssignmentsClient.list(filter.qualificationModuleID, filter.procedureID);

export const createAssignment = (qualificationModuleID: string, procedureVersionID: string): Promise<AssignmentGenerationModel> =>
    procedureAssignmentsClient.create(new CreateProcedureAssignmentModel({ qualificationModuleID, procedureVersionID }));

export const activateAssignment = (id: string): Promise<AssignmentGenerationModel> => procedureAssignmentsClient.activate(id);

export const deactivateAssignment = (id: string): Promise<AssignmentGenerationModel> => procedureAssignmentsClient.deactivate(id);

/** Re-expands the rules into edges. Needed after the assessment structure underneath changes. */
export const regenerateAssignment = (id: string): Promise<AssignmentGenerationModel> => procedureAssignmentsClient.regenerate(id);

export const upgradeAssignment = (id: string, procedureVersionID: string): Promise<AssignmentGenerationModel> =>
    procedureAssignmentsClient.upgrade(id, new UpgradeProcedureAssignmentModel({ procedureVersionID }));

export const previewAssignment = (id: string): Promise<CalculationPreviewModel> => procedureAssignmentsClient.preview(id);

// ---------------------------------------------------------------- calculations

export interface RunOptions {
    /** Required. Omitting it is a 400, not a default. */
    saveResults: boolean;
    /**
     * Required. Whether a calculated value may overwrite a manually entered one. Whether the caller
     * is *allowed* to is decided server-side and overwritten on arrival - this only expresses intent.
     */
    overrideManualMarks: boolean;
}

export const runCalculations = (scopes: CalculationScope[], options: RunOptions): Promise<CalculationRunSummaryModel> =>
    calculationsClient.run(
        new ScopedCalculationRequest({
            scopes: toScopeModels(scopes),
            options: new CalculationRunOptionsModel(options),
        })
    );

export const previewCalculations = (
    scopes: CalculationScope[],
    options: RunOptions,
    page = 1,
    pageSize = 50
): Promise<CalculationPreviewPageModel> =>
    calculationsClient.preview(
        new CalculationPreviewPageRequest({
            scopes: toScopeModels(scopes),
            options: new CalculationRunOptionsModel(options),
            page,
            // The server caps this at 200.
            pageSize: Math.min(pageSize, 200),
        })
    );

// -------------------------------------------------------------------- findings

export interface FindingQuery {
    severity?: string;
    code?: string;
    qualificationModuleID?: string;
    assignmentID?: string;
    studentID?: string;
    page?: number;
    pageSize?: number;
}

/** Findings are computed on demand and never persisted, so this is always a live view. */
export const listFindings = (query: FindingQuery = {}): Promise<CalculationFindingPageModel> =>
    calculationFindingsClient.get(
        query.severity,
        query.code,
        query.qualificationModuleID,
        query.assignmentID,
        query.studentID,
        query.page ?? 1,
        query.pageSize ?? 50
    );

export type { CalculationExecutionModel, CalculationPreviewModel };
